import { useCallback, useEffect, useRef, useState } from 'react';
import { useOgTenantInfo } from '@atlassian/jira-eoc-tenant-info/src/controllers/index.tsx';
import { ff } from '@atlassian/jira-feature-flagging';
import { useFlagsService } from '@atlassian/jira-flags';
import { useOpsgenieBaseUrl } from '@atlassian/jira-platform-get-og-base-url/src/main.tsx';
import { SERVICE_DESK } from '@atlassian/jira-shared-types/src/application-key.tsx';
import { FREE_EDITION, STANDARD_EDITION } from '@atlassian/jira-shared-types/src/edition.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { useQueryParam } from '@atlassian/react-resource-router';
import { createStore, createHook } from '@atlassian/react-sweet-state';
import { useChatToolsStore } from '../../../services/store/chat-tools/index.tsx';
import { useDeprecatedCountsStore } from '../../../services/store/deprecated-counts/index.tsx';
import { useIncidentsStore } from '../../../services/store/incidents/index.tsx';
import { useIntegrationsStore } from '../../../services/store/integrations/index.tsx';
import { useJNAAppStatus } from '../../../services/store/jna-app-status/index.tsx';
import { useTeamAdminStatus } from '../../../services/store/team-admin-status/index.tsx';
import { useTeams } from '../../../services/store/teams/index.tsx';
import { useTransitioningStatus } from '../../../services/store/transitioning-status/index.tsx';
import { useUsersAndRolesStore } from '../../../services/store/users-and-roles/index.tsx';
import { multiplePromiseSettler } from '../../../services/store/utils/index.tsx';
import { useOgConsolidationUserState } from '../../use-og-consolidation-user-state/index.tsx';
import { REF_QUERY_PARAM, REF_QUERY_PARAM_VALUE } from './constants.tsx';
import messages from './messages.tsx';
import type { GlobalChangeboardingState } from './types.tsx';

const initialState: GlobalChangeboardingState = {
	isTransitionHomeActivated: false,
	isTransitionHomeModalOpen: false,
	isWelcomeModalActivated: false,
	opsgenieBaseUrl: undefined,
};

const Store = createStore({
	initialState,
	actions: {
		openTransitionHomeModalAction:
			() =>
			({ setState }) => {
				setState({ isTransitionHomeModalOpen: true });
			},
		closeTransitionHomeModal:
			() =>
			({ setState }) => {
				setState({ isTransitionHomeModalOpen: false });
			},
		activateTransitionHome:
			() =>
			({ setState }) => {
				setState({ isTransitionHomeActivated: true });
			},
		activateWelcomeModal:
			() =>
			({ setState }) => {
				setState({ isWelcomeModalActivated: true });
			},
		deactivateWelcomeModal:
			() =>
			({ setState }) => {
				setState({ isWelcomeModalActivated: false });
			},
	},
	name: 'global-changeboarding',
});

const useGlobalChangeboardingStore = createHook(Store);

const useGlobalChangeboarding = () => {
	const [isTransitionHomeLoading, setIsTransitionHomeLoading] = useState(false);
	const [
		{ isTransitionHomeActivated, isWelcomeModalActivated, isTransitionHomeModalOpen },
		{
			openTransitionHomeModalAction,
			closeTransitionHomeModal,
			activateTransitionHome,
			activateWelcomeModal,
			deactivateWelcomeModal,
		},
	] = useGlobalChangeboardingStore();
	const [
		{ usersWithoutAdminRole, usersWithoutCustomRole, stakeholders },
		{ initialize: initializeUsersAndRoles },
	] = useUsersAndRolesStore();

	const [
		{
			counts,
			syncedIntegrations,
			discontinuedIntegrations,
			deprecatedIntegrations,
			incomingCallIntegrations,
		},
		{ initialize: initializeIntegrations },
	] = useIntegrationsStore();

	const [{ notificationPolicies, groups, heartbeats }, { initialize: initializeDeprecatedCounts }] =
		useDeprecatedCountsStore();

	const { showFlag } = useFlagsService();
	const [
		{ integrations, connections },
		{ initialize: initializeChatTools, initializeForTeamAdmin: initializeChatToolsForTeamAdmin },
	] = useChatToolsStore();

	const [{ incidentsCopyStatus }, { initialize: initializeIncidentsCopyStatus }] =
		useIncidentsStore();

	const [
		{ loading: transitioningStatusLoading, error: transitioningStatusError },
		{ initialize: initializeTransitioningStatus },
	] = useTransitioningStatus();

	const tenantContext = useTenantContext();
	const appEditions = useAppEditions();

	const hasJsmAboveStandard =
		!!tenantContext.licensedProducts[SERVICE_DESK] &&
		appEditions.serviceDesk !== FREE_EDITION &&
		appEditions.serviceDesk !== STANDARD_EDITION;

	const [{ teams }, { initialize: initializeTeams }] = useTeams();

	const [
		{ loading: jnaAppStatusLoading, error: jnaAppStatusError },
		{ initialize: initializeJNAAppStatus },
	] = useJNAAppStatus();

	const [{ teamAdminStatus }, { initialize: initializeTeamAdminStatus }] = useTeamAdminStatus();

	const [{ fetchStatus, opsgenieBaseUrl }] = useOpsgenieBaseUrl('eocChangeboarding');
	const isOpsgenieBaseUrlLoaded = fetchStatus === 'SUCCESS';

	const [ref] = useQueryParam(REF_QUERY_PARAM);
	const [isWelcomeModalOpen, setIsWelcomeModalOpen] = useState(true);
	const { data, error } = useOgConsolidationUserState();
	const { isTransitioning, loading: tenantLoading, error: tenantError } = useOgTenantInfo();
	const isAdmin = useIsAdmin();
	const isSiteAdmin = useIsSiteAdmin();

	const isWelcomeModalDisplayedInitialValue = useRef(data?.isWelcomeModalDisplayed);
	isWelcomeModalDisplayedInitialValue.current =
		isWelcomeModalDisplayedInitialValue.current ?? data?.isWelcomeModalDisplayed;

	const isTransitioningInitialValue = useRef(isTransitioning);
	isTransitioningInitialValue.current = isTransitioningInitialValue.current ?? isTransitioning;

	// Both of the waiting states are loaded at the SSR and should be already fetched
	// when client starts to render but just in case we should check it
	const isAllWelcomeModalDataLoaded =
		isWelcomeModalOpen &&
		!tenantLoading &&
		!tenantError &&
		// We need to wait for the data to decide on the final state
		data?.isWelcomeModalDisplayed !== undefined &&
		isOpsgenieBaseUrlLoaded;

	// Do not show the welcome modal until everything is ready
	// This will also prevent a render if there is an error with the initial
	// loading.
	// // We only care for the initial value, it should not change if the
	// // displayed state changes later.
	const finalWelcomeModalIsOpen =
		isAllWelcomeModalDataLoaded &&
		isTransitioningInitialValue.current &&
		!isWelcomeModalDisplayedInitialValue.current;

	const handleCloseWelcomeModal = () => {
		setIsWelcomeModalOpen(false);
	};

	const initializeModalRequests = useCallback(() => {
		const minimalRequiredInitializations = [
			initializeTransitioningStatus(),
			initializeJNAAppStatus(),
		];

		const isUser = !isAdmin && !isSiteAdmin;

		if (isUser) {
			if (ff('platform.show-optimal-tooling_brgom')) {
				const optimalRequiredInitializations = [
					...minimalRequiredInitializations,
					initializeTeamAdminStatus(),
					initializeChatToolsForTeamAdmin(),
					initializeIntegrations(),
				];

				return Promise.allSettled(optimalRequiredInitializations).then(multiplePromiseSettler);
			}

			return Promise.allSettled(minimalRequiredInitializations).then(multiplePromiseSettler);
		}

		const minimalToolingRequests = [
			initializeChatTools(),
			initializeUsersAndRoles(hasJsmAboveStandard),
			...minimalRequiredInitializations,
		];

		if (ff('platform.show-optimal-tooling_brgom')) {
			const optimalToolingRequests = [
				...minimalToolingRequests,
				initializeTeams(),
				initializeTeamAdminStatus(),
				initializeIntegrations(),
				initializeDeprecatedCounts(),
				initializeIncidentsCopyStatus(),
			];

			return Promise.allSettled(optimalToolingRequests).then(multiplePromiseSettler);
		}

		return Promise.allSettled(minimalToolingRequests).then(multiplePromiseSettler);
	}, [
		initializeTransitioningStatus,
		initializeJNAAppStatus,
		isAdmin,
		isSiteAdmin,
		initializeChatTools,
		initializeUsersAndRoles,
		hasJsmAboveStandard,
		initializeTeamAdminStatus,
		initializeChatToolsForTeamAdmin,
		initializeTeams,
		initializeIntegrations,
		initializeDeprecatedCounts,
		initializeIncidentsCopyStatus,
	]);

	useEffect(() => {
		// Close the modal if an error occurs after the initial load
		// If the initial load errors out we don't render the modal at all.
		// This means we generated an error later in the process, let's just
		// get rid of the welcome modal.
		if (data && error) {
			setIsWelcomeModalOpen(false);
		}
	}, [error, data]);

	useEffect(() => {
		// if the decision data is not loaded don't activate anything
		if (!isAllWelcomeModalDataLoaded) {
			return;
		}

		// If one of the request failed then silently fail and activate transition home
		if (
			usersWithoutAdminRole.error ||
			usersWithoutCustomRole.error ||
			integrations.error ||
			connections.error ||
			transitioningStatusError ||
			jnaAppStatusError ||
			(ff('platform.show-optimal-tooling_brgom') &&
				(teams.error ||
					teamAdminStatus.error ||
					counts.error ||
					syncedIntegrations.error ||
					discontinuedIntegrations.error ||
					deprecatedIntegrations.error ||
					incomingCallIntegrations.error ||
					heartbeats.error ||
					notificationPolicies.error ||
					groups.error ||
					incidentsCopyStatus.error ||
					(hasJsmAboveStandard && stakeholders.error)))
		) {
			deactivateWelcomeModal();
			activateTransitionHome();
			return;
		}

		// if one of the flow is activated or requests are loading then don't activate anything more
		if (
			isWelcomeModalActivated ||
			isTransitionHomeActivated ||
			usersWithoutAdminRole.loading ||
			usersWithoutCustomRole.loading ||
			integrations.loading ||
			connections.loading ||
			transitioningStatusLoading ||
			jnaAppStatusLoading ||
			(ff('platform.show-optimal-tooling_brgom') &&
				(teams.loading ||
					teamAdminStatus.loading ||
					counts.loading ||
					syncedIntegrations.loading ||
					discontinuedIntegrations.loading ||
					deprecatedIntegrations.loading ||
					incomingCallIntegrations.loading ||
					heartbeats.loading ||
					notificationPolicies.loading ||
					groups.loading ||
					incidentsCopyStatus.loading ||
					(hasJsmAboveStandard && stakeholders.loading)))
		) {
			return;
		}

		// if the ref is exist on query parameter, we should activate the modal
		if (ref === REF_QUERY_PARAM_VALUE && finalWelcomeModalIsOpen) {
			initializeModalRequests().then(() => {
				activateWelcomeModal();
			});

			return;
		}
		// if the ref is not present or we should not display welcome modal and the user is in transitioning state at first then activate transition home
		if (isTransitioningInitialValue.current) {
			deactivateWelcomeModal();
			activateTransitionHome();
		}
	}, [
		activateTransitionHome,
		activateWelcomeModal,
		connections.loading,
		deactivateWelcomeModal,
		finalWelcomeModalIsOpen,
		initializeUsersAndRoles,
		integrations.loading,
		isAllWelcomeModalDataLoaded,
		isTransitionHomeActivated,
		isWelcomeModalActivated,
		ref,
		initializeModalRequests,
		usersWithoutAdminRole.loading,
		usersWithoutCustomRole.loading,
		counts.loading,
		syncedIntegrations.loading,
		discontinuedIntegrations.loading,
		deprecatedIntegrations.loading,
		incomingCallIntegrations.loading,
		transitioningStatusLoading,
		jnaAppStatusLoading,
		usersWithoutAdminRole.error,
		usersWithoutCustomRole.error,
		integrations.error,
		connections.error,
		transitioningStatusError,
		jnaAppStatusError,
		teams.error,
		teams.loading,
		teamAdminStatus.error,
		teamAdminStatus.loading,
		counts.error,
		syncedIntegrations.error,
		discontinuedIntegrations.error,
		deprecatedIntegrations.error,
		incomingCallIntegrations.error,
		heartbeats.error,
		notificationPolicies.error,
		groups.error,
		heartbeats.loading,
		notificationPolicies.loading,
		groups.loading,
		incidentsCopyStatus.loading,
		incidentsCopyStatus.error,
		stakeholders.error,
		stakeholders.loading,
		hasJsmAboveStandard,
	]);

	const openTransitionHomeModal = () => {
		setIsTransitionHomeLoading(true);
		return initializeModalRequests()
			.then(() => {
				activateTransitionHome();
				openTransitionHomeModalAction();
			})
			.catch(() => {
				showFlag({
					type: 'error',
					title: messages.errorTitleNonFinal,
					description: messages.errorDescriptionNonFinal,
					actions: [{ content: messages.errorActionNonFinal, onClick: openTransitionHomeModal }],
				});
			})
			.finally(() => {
				setIsTransitionHomeLoading(false);
			});
	};

	return {
		opsgenieBaseUrl,
		openTransitionHomeModal,
		closeTransitionHomeModal,
		activateTransitionHome,
		isTransitionHomeLoading,
		isTransitionHomeActivated,
		isWelcomeModalActivated,
		isTransitionHomeModalOpen,
		finalWelcomeModalIsOpen,
		handleCloseWelcomeModal,
		activateWelcomeModal,
	};
};

export { useGlobalChangeboarding, useGlobalChangeboardingStore };
